import React, { useState } from "react";
import { Box, TextField, IconButton, Typography, Card, CardContent, Chip, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ClearIcon from "@mui/icons-material/Clear";
import { Delivery } from "../../../../Models/Delivery";
import { AdminAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/AdminAPIHandler";
import "./AdminDeliveriesPage.scss";

const AdminDeliveriesPage: React.FC = () => {
  const [deliveryId, setDeliveryId] = useState("");
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");

  const clearResults = () => {
    setDelivery(null);
    setStatusMessage("");
    setDeliveryId("");
  };

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(`${label} copied!`);
      setTimeout(() => setCopyFeedback(""), 2000);
    } catch (error) {
      setCopyFeedback("Failed to copy");
      setTimeout(() => setCopyFeedback(""), 2000);
    }
  };

  const fetchDeliveryById = async () => {
    if (!deliveryId.trim()) {
      setStatusMessage("Please enter a delivery ID");
      return;
    }
    
    setIsLoading(true);
    setStatusMessage("");
    try {
      const fetchedDelivery = await AdminAPIHandler.shared.getDeliveryById(deliveryId);
      setDelivery(fetchedDelivery);
      setStatusMessage(`Found delivery`);
    } catch (error) {
      setDelivery(null);
      setStatusMessage(`Error: ${error}`);
    }
    setIsLoading(false);
  };

  const formatDate = (date?: Date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Copyable Field Component
  const CopyableField = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      gap: 1,
      padding: '8px 0',
      borderBottom: '1px solid #f1f5f9'
    }}>
      <Typography component="span" sx={{ fontWeight: 600, color: '#64748b', minWidth: '120px' }}>
        {label}:
      </Typography>
      <Typography component="span" sx={{ flex: 1, wordBreak: 'break-word' }}>
        {value}
      </Typography>
      <IconButton
        size="small"
        onClick={() => handleCopyToClipboard(value, label)}
        sx={{ 
          color: '#64748b',
          '&:hover': { color: '#3b82f6' }
        }}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Search Controls */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 2,
        p: 3,
        background: '#f8fafc',
        borderRadius: 2,
        border: '1px solid #e2e8f0'
      }}>
        {/* Search by Delivery ID */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by Delivery ID"
            value={deliveryId}
            onChange={(e) => setDeliveryId(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchDeliveryById()}
            disabled={isLoading}
            sx={{ minWidth: '200px' }}
          />
          <IconButton
            onClick={fetchDeliveryById}
            disabled={!deliveryId.trim() || isLoading}
            sx={{
              background: '#3b82f6',
              color: 'white',
              '&:hover': {
                background: '#2563eb'
              },
              '&:disabled': {
                background: '#e2e8f0',
                color: '#94a3b8'
              }
            }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Copy Feedback */}
      {copyFeedback && (
        <Box sx={{ 
          p: 1.5, 
          background: '#dcfce7',
          color: '#166534',
          borderRadius: 2,
          fontWeight: 500,
          textAlign: 'center'
        }}>
          {copyFeedback}
        </Box>
      )}

      {/* Status Message */}
      {statusMessage && (
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2, 
          background: statusMessage.startsWith('Error') ? '#fee2e2' : '#dcfce7',
          color: statusMessage.startsWith('Error') ? '#991b1b' : '#166534',
          borderRadius: 2,
          fontWeight: 500
        }}>
          <Typography>{statusMessage}</Typography>
          <Button
            startIcon={<ClearIcon />}
            onClick={clearResults}
            size="small"
            sx={{
              color: statusMessage.startsWith('Error') ? '#991b1b' : '#166534',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: statusMessage.startsWith('Error') ? '#fecaca' : '#bbf7d0'
              }
            }}
          >
            Clear Results
          </Button>
        </Box>
      )}

      {/* Delivery Display */}
      {delivery && (
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#1e293b' }}>
              📦 Delivery Details
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {/* Delivery ID */}
              <CopyableField label="Delivery ID" value={delivery.id} />

              {/* Order ID */}
              <CopyableField label="Order ID" value={delivery.order} />

              {/* Buyer ID */}
              <CopyableField label="Buyer ID" value={delivery.metaData.buyerId} />

              {/* Seller ID */}
              <CopyableField label="Seller ID" value={delivery.metaData.sellerId} />

              {/* Gig ID */}
              <CopyableField label="Gig ID" value={delivery.metaData.gigId} />

              {/* Channel ID */}
              <CopyableField label="Channel ID" value={delivery.metaData.channelId} />

              {/* Remarks */}
              {delivery.remarks && (
                <CopyableField label="Remarks" value={delivery.remarks} />
              )}

              {/* Created At */}
              <CopyableField label="Created At" value={formatDate(delivery.createdAt)} />

              {/* Updated At */}
              <CopyableField label="Updated At" value={formatDate(delivery.updatedAt)} />

              {/* Deliverables */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: '#1e293b' }}>
                  📁 Deliverables ({delivery.deliverables.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {delivery.deliverables.map((deliverable, index) => (
                    <Card key={index} sx={{ 
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      p: 2
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight={600}>{deliverable.fileName}</Typography>
                          <Chip 
                            label={deliverable.fileType} 
                            size="small" 
                            sx={{ 
                              background: '#dbeafe',
                              color: '#1e40af'
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Size: {formatFileSize(deliverable.fileSizeInBytes)}
                        </Typography>
                        {deliverable.mediaUrl && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#3b82f6',
                              wordBreak: 'break-all'
                            }}
                          >
                            {deliverable.mediaUrl}
                          </Typography>
                        )}
                      </Box>
                    </Card>
                  ))}
                </Box>
              </Box>

              {/* Preview Screenshots */}
              {delivery.previewScreenshots && delivery.previewScreenshots.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: '#1e293b' }}>
                    🖼️ Preview Screenshots ({delivery.previewScreenshots.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {delivery.previewScreenshots.map((screenshot, index) => (
                      <img
                        key={index}
                        src={screenshot}
                        alt={`Screenshot ${index + 1}`}
                        style={{
                          width: '200px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !delivery && !statusMessage && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          color: '#94a3b8'
        }}>
          <Typography variant="h6" gutterBottom>
            No delivery to display
          </Typography>
          <Typography variant="body2">
            Use the search options above to find a delivery
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AdminDeliveriesPage;
